import React, { useState, useMemo, useCallback } from 'react';
import { TicketContext } from './TicketContext';
import TicketService from '../../../services/ticketing/TicketingServices';

export function TicketProvider({ children }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      setTicketsLoading(true);
      const response = await TicketService.getTickets();
      //console.log('API Response:', response.data); // Debug log

      // Handle the API response structure from ticket/tickets/
      let ticketsArray = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          // If response.data is directly an array
          ticketsArray = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // If response has pagination structure with data field
          ticketsArray = response.data.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // If response has results field
          ticketsArray = response.data.results;
        } else if (response.data.tickets && Array.isArray(response.data.tickets)) {
          // If response has tickets field
          ticketsArray = response.data.tickets;
        }
      }

      setTickets(ticketsArray);
      setError('');
    } catch (err) {
      //console.error('Error fetching tickets:', err);
      // Fallback data when API is not available
      if (err.response?.status === 404) {
        //console.log('Ticketing API not available, using fallback data');
        setTickets([]); // Empty array for now
        setError(''); // Don't show error for 404, just use empty state
      } else {
        setError(err.message || 'Failed to fetch tickets');
        setTickets([]);
      }
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  const getTicketById = useCallback(async (ticketId) => {
    try {
      setLoading(true);
      const response = await TicketService.getTicketById(ticketId);
      //console.log('Single ticket API Response:', response.data); // Debug log

      let ticketData = null;
      if (response.data) {
        // Handle different response structures
        if (response.data.data) {
          ticketData = response.data.data;
        } else {
          ticketData = response.data;
        }
      }

      // Add the ticket to the local tickets array if it's not already there
      if (ticketData) {
        setTickets(prevTickets => {
          const existingIndex = prevTickets.findIndex(t =>
            t.id === ticketData.id || t.ticket_id === ticketData.ticket_id
          );

          if (existingIndex === -1) {
            // Ticket not found, add it
            return [...prevTickets, ticketData];
          } else {
            // Ticket exists, update it
            const updated = [...prevTickets];
            updated[existingIndex] = ticketData;
            return updated;
          }
        });
      }

      setError('');
      return ticketData;
    } catch (err) {
      //console.error('Error fetching single ticket:', err);
      setError(err.message || 'Failed to fetch ticket');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await TicketService.getDashboardStats();
      const data = response.data;
      setStats(data);
      setError('');
    } catch (err) {
      //console.error('Error fetching stats:', err);
      // Fallback data when API is not available
      if (err.response?.status === 404) {
        //console.log('Stats API not available, using fallback data');
        setStats({}); // Empty object for now
        setError(''); // Don't show error for 404
      } else if (err.response?.status === 500) {
        //console.log('Server error while fetching stats, using fallback data');
        setStats({}); // Empty object for server errors
        setError(''); // Don't show error for 500, just use empty state
      } else {
        // For other errors (network, authentication, etc.)
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch dashboard statistics';
        setError(errorMessage);
        setStats({}); // Ensure stats is empty on error
      }
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const createTicket = async (ticketData) => {
    setLoading(true);
    try {
      console.log("API Request Payload:", ticketData); // Log payload
      const response = await TicketService.createTicket(ticketData);
      console.log("Full API Response:", response);
      const data = response.data;
      console.log('Ticket created successfully:', data); // Debug log
      setError('');

      // Validate that we got a valid response
      if (!data) {
        throw new Error('Server returned empty response');
      }

      // Add a small delay to ensure the backend has processed the ticket
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh tickets after creating
      await fetchTickets();
      return data;
    } catch (err) {
      console.error('Error creating ticket:', err); // Debug log
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create ticket';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId, ticketData) => {
    setLoading(true);
    try {
      const response = await TicketService.updateTicket(ticketId, ticketData);
      const data = response.data;
      setError('');
      // Refresh tickets after updating
      await fetchTickets();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTicket = async (ticketId) => {
    setLoading(true);
    try {
      await TicketService.deleteTicket(ticketId);
      setError('');
      // Refresh tickets after deleting
      await fetchTickets();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const uploadAttachment = async (ticketId, file) => {
    try {
      const response = await TicketService.uploadAttachment(ticketId, file);
      console.log('Attachment uploaded successfully:', response.data); // Debug log
      return response.data;
    } catch (err) {
      console.error('Error uploading attachment:', err);
      throw new Error(err.message || 'Failed to upload attachment');
    }
  };

  const getCategories = async () => {
    try {
      const response = await TicketService.getCategories();
      const data = response.data;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getSubcategories = async (categoryId) => {
    try {
      const response = await TicketService.getSubcategories(categoryId);
      const data = response.data;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const getAssignableUsers = async (page = 1) => {
    try {
      let allUsers = [];
      let currentPage = page;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await TicketService.getAssignableUsers(currentPage);
        const data = response.data;

        // Ensure data.data is an array
        const userArray = Array.isArray(data.data) ? data.data : [];

        // Format users
        const formattedUsers = userArray.map(user => ({
          user: user.user,
          name: user.name,
          email: user.user_email
        }));

        // Append to allUsers
        allUsers = [...allUsers, ...formattedUsers];

        // Check pagination metadata (adjust field names if necessary)
        const total = data.total || data.total_count || 0;
        const perPage = data.per_page || 10;
        const totalPages = Math.ceil(total / perPage);
        hasMorePages = currentPage < totalPages;
        currentPage++;
      }

      console.log('All assignable users:', allUsers);
      return { data: allUsers, total: allUsers.length };
    } catch (err) {
      console.error('Error fetching assignable users:', err);
      setError(err.message || 'Failed to fetch assignable users');
      throw err;
    }
  };


  const escalateTicket = async (ticketId) => {
    setLoading(true);
    try {
      await TicketService.escalateTicket(ticketId);
      setError('');
      await fetchTickets();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resolveTicket = async (ticketId) => {
    setLoading(true);
    try {
      await TicketService.resolveTicket(ticketId);
      setError('');
      await fetchTickets();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const closeTicket = async (ticketId) => {
    setLoading(true);
    try {
      await TicketService.closeTicket(ticketId);
      setError('');
      await fetchTickets();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getTicketComments = async (ticketId) => {
    try {
      const response = await TicketService.getTicketComments(ticketId);
      const data = response.data;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const addTicketComment = async (ticketId, commentData) => {
    setLoading(true);
    try {
      const response = await TicketService.addTicketComment(ticketId, commentData);
      const data = response.data;
      setError('');
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getTicketAttachments = async (ticketId) => {
    try {
      const response = await TicketService.getTicketAttachments(ticketId);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching attachments:', err);
      return [];
    }
  };

  const actions = useMemo(() => ({
    fetchTickets,
    getTicketById,
    fetchStats,
    createTicket,
    updateTicket,
    deleteTicket,
    uploadAttachment,
    getCategories,
    getSubcategories,
    escalateTicket,
    resolveTicket,
    closeTicket,
    getTicketComments,
    addTicketComment,
    getTicketAttachments,
    getAssignableUsers
  }), [fetchTickets, getTicketById, fetchStats, getAssignableUsers]);

  // Only show loading when both tickets and stats are loading initially
  // This prevents the UI from showing loading state after data is already available
  const combinedLoading = (ticketsLoading && tickets.length === 0) || (statsLoading && Object.keys(stats).length === 0) || loading;

  return (
    <TicketContext.Provider value={{ tickets, stats, loading: combinedLoading, error, actions }}>
      {children}
    </TicketContext.Provider>
  );
}
